import React, { FC } from 'react';

import { Alert, Button, Descriptions, Flex, Layout, Result, Row, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { getResponseStatus, isForbiddenError } from '@app/services/apiErrors';
import { formatDate, formatDateAndTimeShort, fromUtc } from '@app/services/date';
import { hasHistory } from '@app/services/hasHistory';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { AccessDenied } from '@components/AccessDenied';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';
import { OrderTabs } from '@components/OrderTabs';
import { Paper } from '@components/Paper';

import { DeliveryAddress } from '../../components/DeliveryAddress';
import { ModuleIcon } from '../../components/ModuleIcon';
import { OrderStatusTag } from '../../components/OrderStatusTag';
import { Places } from '../../components/Places';
import { formatDecimal } from '../../data/cargo';
import { useCounterpartyOrderQuery } from '../../hooks';
import { COUNTERPARTY_ORDERS_PATH } from '../../interfaces';

export const Single: FC = () => {
  const { uid = '' } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const ordersT = useTranslation(NSKeys.counterpartyOrders);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const order = useCounterpartyOrderQuery(uid);
  const locale = ordersT.i18n.language;
  const isUnavailable = getResponseStatus(order.error) === 404;

  const goBack = () => (hasHistory() ? navigate(-1) : navigate(COUNTERPARTY_ORDERS_PATH));

  const renderContent = () => {
    if (order.isLoading) {
      return <Skeleton active />;
    }
    if (isUnavailable) {
      return (
        <Result
          status="404"
          title={ordersT.t('card.unavailableTitle')}
          subTitle={ordersT.t('card.unavailableText')}
          extra={
            <Button type="primary" onClick={() => navigate(COUNTERPARTY_ORDERS_PATH)}>
              {ordersT.t('card.backToList')}
            </Button>
          }
        />
      );
    }
    if (isForbiddenError(order.error)) {
      return <AccessDenied withProfileLink={false} />;
    }
    if (!order.data) {
      return (
        <Result
          status="error"
          subTitle={getErrorMessage(order.error)}
          extra={<Button onClick={() => order.refetch()}>{commonT.t('buttonReload')}</Button>}
        />
      );
    }

    const { customer, delivery_city, delivery_address, status, modules, cargo, cargo_unavailable } = order.data;
    const cargoValue = (value: React.ReactNode) =>
      cargo_unavailable ? <Typography.Text type="secondary">{ordersT.t('cargoUnavailable')}</Typography.Text> : value;

    return (
      <Flex vertical gap={16}>
        {cargo_unavailable && <Alert type="warning" showIcon title={ordersT.t('cargoUnavailableAlert')} />}
        <Descriptions
          bordered
          size="small"
          column={1}
          items={[
            { key: 'customer', label: ordersT.t('columns.customer'), children: customer.title },
            { key: 'recipient', label: ordersT.t('columns.recipient'), children: order.data.recipient_name || '—' },
            {
              key: 'delivery',
              label: ordersT.t('columns.delivery'),
              children: <DeliveryAddress city={delivery_city} address={delivery_address} />,
            },
            {
              key: 'contact',
              label: ordersT.t('card.contact'),
              children: [order.data.contact_name, order.data.contact_phone].filter(Boolean).join(', ') || '—',
            },
            {
              key: 'comment',
              label: ordersT.t('card.comment'),
              children: <span style={{ whiteSpace: 'pre-wrap' }}>{order.data.comment || '—'}</span>,
            },
            {
              key: 'createdAt',
              label: ordersT.t('columns.createdAt'),
              children: formatDateAndTimeShort(fromUtc(order.data.created_at)),
            },
            {
              key: 'deliveryDate',
              label: ordersT.t('columns.deliveryDate'),
              children: formatDate(order.data.delivery_date),
            },
            { key: 'status', label: ordersT.t('columns.status'), children: <OrderStatusTag name={status.name} /> },
            {
              key: 'modules',
              label: ordersT.t('card.modules'),
              children: (
                <Flex vertical gap={8}>
                  {modules.map((item) => (
                    <Flex key={item.module} align="center" gap={8}>
                      <ModuleIcon module={item.module} />
                      <Typography.Text>{commonT.t(`module.${item.module}` as const)}</Typography.Text>
                      {item.status && <OrderStatusTag status={item.status} />}
                    </Flex>
                  ))}
                </Flex>
              ),
            },
            {
              key: 'places',
              label: ordersT.t('columns.places'),
              children: cargoValue(<Places places={cargo?.places} align="flex-start" />),
            },
            {
              key: 'weight',
              label: ordersT.t('columns.weight'),
              children: cargoValue(formatDecimal(cargo?.weight, locale)),
            },
            {
              key: 'volume',
              label: ordersT.t('columns.volume'),
              children: cargoValue(formatDecimal(cargo?.volume, locale)),
            },
          ]}
        />
      </Flex>
    );
  };

  const title = order.data ? ordersT.t('card.title', { num: order.data.num }) : ordersT.t('title');

  return (
    <Layout>
      <Layout.Header style={{ border: 0 }}>
        <Breadcrumbs
          apiTitles={[
            { id: ':counterpartyOrderId', value: order.data ? ordersT.t('number', { num: order.data.num }) : uid },
          ]}
        />
        <Row justify="space-between" align="middle">
          <HeaderTitle loading={order.isLoading} title={title} onClick={goBack} />
        </Row>
      </Layout.Header>

      <OrderTabs orderUid={order.data?.uid || ''}>
        <Paper style={{ margin: 24, padding: 24 }}>{renderContent()}</Paper>
      </OrderTabs>
    </Layout>
  );
};
